import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { 
  Download, Upload, FileSpreadsheet, CheckCircle, 
  AlertCircle, Loader2, FileDown, Table, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminImport = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'sunify_products.xlsx';
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
        
        toast.success(t('admin.import_page.export_ok', 'Export úspešný!'));
      } else {
        toast.error('Chyba pri exporte');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Chyba pri exporte');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/products/template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'sunify_products_template.xlsx';
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
        
        toast.success(t('admin.import_page.template_ok', 'Šablóna stiahnutá!'));
      } else {
        toast.error(t('admin.import_page.template_error', 'Chyba pri sťahovaní šablóny'));
      }
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Chyba pri sťahovaní šablóny');
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.xlsx') && !filename.endsWith('.xls') && !filename.endsWith('.csv')) {
      toast.error(t('admin.import_page.only_excel', 'Povolené sú len Excel (.xlsx, .xls) alebo CSV súbory'));
      return;
    }

    setUploading(true);
    setImportResult(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin/products/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setImportResult(data);
        if (data.created > 0 || data.updated > 0) {
          toast.success(t('admin.import_page.import_ok', 'Import úspešný! Vytvorených: {{c}}, Aktualizovaných: {{u}}', { c: data.created, u: data.updated }));
        }
        if (data.errors?.length > 0) {
          toast.warning(t('admin.import_page.import_warn', 'Import dokončený s {{n}} chybami', { n: data.errors.length }));
        }
      } else {
        toast.error(data.detail || 'Chyba pri importe');
        setImportResult({ errors: [data.detail || 'Unknown error'] });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Chyba pri importe');
      setImportResult({ errors: ['Chyba pripojenia k serveru'] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImport(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImport(e.target.files[0]);
    }
  };

  const csvColumns = [
    { name: 'sku', desc: 'Jedinečný kód produktu', required: true },
    { name: 'brand', desc: 'Názov značky (napr. Armolan)', required: false },
    { name: 'brand_id', desc: 'ID značky z databázy', required: false },
    { name: 'category_id', desc: 'ID kategórie z databázy', required: false },
    { name: 'price_eur', desc: 'Cena v EUR (napr. 29.99)', required: true },
    { name: 'price_usd', desc: 'Cena v USD', required: false },
    { name: 'stock', desc: 'Počet kusov na sklade', required: true },
    { name: 'delivery_days', desc: 'Dodacia lehota (napr. "3-5 dní")', required: false },
    { name: 'is_featured', desc: 'Odporúčaný produkt (yes/no)', required: false },
    { name: 'image_url', desc: 'URL obrázka produktu', required: false },
    { name: 'name_sk', desc: 'Názov v slovenčine', required: true },
    { name: 'name_en', desc: 'Názov v angličtine', required: false },
    { name: 'name_de', desc: 'Názov v nemčine', required: false },
    { name: 'name_cs', desc: 'Názov v češtine', required: false },
    { name: 'name_ru', desc: 'Názov v ruštine', required: false },
    { name: 'description_sk', desc: 'Popis v slovenčine', required: false },
    { name: 'description_en', desc: 'Popis v angličtine', required: false },
    { name: 'description_de', desc: 'Popis v nemčine', required: false },
    { name: 'description_cs', desc: 'Popis v češtine', required: false },
    { name: 'description_ru', desc: 'Popis v ruštine', required: false },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{t('admin.import.title')}</h1>
      </div>

      {/* Export and Template Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export Card */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Download className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold mb-2">{t('admin.import.export_title')}</h2>
              <p className="text-gray-600 text-sm mb-4">
                {t('admin.import.export_desc')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={handleExport}
                  className="bg-green-500 hover:bg-green-600"
                  data-testid="export-products-btn"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {t('admin.import.export_btn')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Template Card */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold mb-2">{t('admin.import.template_title')}</h2>
              <p className="text-gray-600 text-sm mb-4">
                {t('admin.import.template_desc')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  data-testid="download-template-btn"
                >
                  <Table className="h-4 w-4 mr-2" />
                  {t('admin.import.template_btn')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <Upload className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-1">{t('admin.import.import_title')}</h2>
            <p className="text-gray-600 text-sm">
              {t('admin.import.import_desc')}
            </p>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            dragActive 
              ? 'border-orange-500 bg-orange-50' 
              : 'border-gray-300 hover:border-orange-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
              <p className="text-lg font-medium">{t('admin.import.importing')}</p>
              <p className="text-gray-500 text-sm">{t('admin.import.wait')}</p>
            </div>
          ) : (
            <>
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">
                {t('admin.import.drop_zone')}
              </p>
              <p className="text-gray-500 text-sm mb-4">{t('admin.import.or')}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="excel-upload"
                data-testid="excel-file-input"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="select-file-btn"
              >
                {t('admin.import.select_file')}
              </Button>
            </>
          )}
        </div>

        {/* Import Results */}
        {importResult && (
          <div className="mt-6 space-y-4">
            {(importResult.created > 0 || importResult.updated > 0) && (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">{t('admin.import.success')}</p>
                  <p className="text-sm text-green-700">
                    {t('admin.import.created')}: {importResult.created} | {t('admin.import.updated')}: {importResult.updated}
                  </p>
                </div>
              </div>
            )}

            {importResult.errors?.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="font-medium text-red-800">{t('admin.import.errors')} ({importResult.errors.length})</p>
                </div>
                <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV Format Reference */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Info className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold">{t('admin.import.format_title')}</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left font-medium text-gray-700">{t('admin.import.column')}</th>
                <th className="px-4 py-2 text-left font-medium text-gray-700">{t('admin.import.column_desc')}</th>
                <th className="px-4 py-2 text-center font-medium text-gray-700">{t('admin.import.required')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {csvColumns.map((col, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-orange-600">{col.name}</td>
                  <td className="px-4 py-2 text-gray-600">{col.desc}</td>
                  <td className="px-4 py-2 text-center">
                    {col.required ? (
                      <span className="text-red-500 font-medium">{t('admin.import.yes')}</span>
                    ) : (
                      <span className="text-gray-400">{t('admin.import.no')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminImport;
